import moment from 'moment';

// ─── API Response Types ────────────────────────────────────────────────────────

export interface CompaniesRequirementsResponse {
  workerCertifications: WorkerCertification[];
  workerTrainings: WorkerTraining[];
  workerCourses: WorkerCourse[];
  allExemptedCertifications: string[];
  allExemptedTrainings: string[];
  allExemptedCourses: string[];
  certificationGroups: CertificationGroup[];
  trainingGroups: TrainingGroup[];
  learningGroups: LearningGroup[];
  timeZone: TimeZone;
  firstActivatedAt: string | null;
  isCompanyExemptedFromDrugTests: boolean;
  isDrugTestingActiveOnProject: boolean;
}

export interface TimeZone {
  id: string;
  name: string;
  code: string;
  offset: string | null;
  abbreviation: string;
}

export interface WorkerCertification {
  id: string;
  idNumber: string | null;
  completionDate: string;
  expirationDate: string | null;
  createdAt: string;
  lastUpdatedAt: string;
  description: string | null;
  certification: { id: string; name: string; isExpirationDateMandatory?: boolean };
  project: { id: string; name: string };
  alias: string | null;
  aliasesInProjects: any;
}

export interface WorkerTraining {
  id: string;
  completionDate: string;
  expirationDate?: string | null;
  training: { id: string; name: string; alias?: string };
  trainerName: string;
  project: { id: string; name: string };
  alias?: string | null;
  aliasesInProjects: any;
}

export interface WorkerCourse {
  courseId: string;
  courseName: string;
  description: string;
  courseStatusName: string;
  courseCompletedPercentage: number;
  isCourseCompleted: boolean;
  expirationDate: string | null;
  courseValidityInDays: number;
}

export interface RequiredItem {
  id: string;
  name: string;
  alias?: string | null;
}

export interface CertificationGroup {
  id: string;
  name: string;
  validationType: number;
  certifications: RequiredItem[];
  exemptedCertifications: string[];
  effectiveDate: string;
  isExistingWorkerForGroup: boolean;
  existingWorkerMandatoryDeadline: string | null;
  existingWorkerGracePeriodInDays: number | null;
  newWorkerMandatoryDeadline: string | null;
  newWorkerGracePeriodInDays: number | null;
}

export interface TrainingGroup {
  id: string;
  name: string;
  validationType: number;
  trainings: RequiredItem[];
  exemptedTrainings: string[];
  effectiveDate: string;
  isExistingWorkerForGroup: boolean;
  existingWorkerMandatoryDeadline: string | null;
  existingWorkerGracePeriodInDays: number | null;
  newWorkerMandatoryDeadline: string | null;
  newWorkerGracePeriodInDays: number | null;
}

export interface LearningGroupCourse {
  id: string;
  courseId: string;
  courseName: string;
  alias: string;
  learningGroupId: string;
  courseValidityInDays: number;
  expirationDate: string | null;
}

export interface LearningGroup {
  id: string;
  name: string;
  validationType: number;
  learningGroupCourses: LearningGroupCourse[];
  exemptedLearnings: string[];
  effectiveDate: string;
  isExistingWorkerForGroup: boolean;
  existingWorkerMandatoryDeadline: string | null;
  existingWorkerGracePeriodInDays: number | null;
  newWorkerMandatoryDeadline: string | null;
  newWorkerGracePeriodInDays: number | null;
}

// ─── Output Types ──────────────────────────────────────────────────────────────

export interface MissingItem {
  id: string;
  name: string;
  groupName: string;
  mandatoryLabel: string;
}

export interface UpcomingGroup {
  effectiveDate: string;
  isExistingWorkerForGroup: boolean;
  existingWorkerGracePeriodInDays: number | null;
  newWorkerGracePeriodInDays: number | null;
  existingWorkerMandatoryDeadline: string | null;
  newWorkerMandatoryDeadline: string | null;
  workerBadgeFirstActivatedAt: string | null;
  items: MissingItem[];
}

export interface ComputedRequirements {
  missingCertifications: MissingItem[];
  missingCourses: MissingItem[];
  missingTrainings: MissingItem[];
  upcomingCertifications: UpcomingGroup[];
  upcomingCourses: UpcomingGroup[];
  upcomingTrainings: UpcomingGroup[];
}

// ─── Helpers ───────────────────────────────────────────────────────────────────

const MANDATORY_LABELS: Record<number, string> = {
  2: 'At least one is mandatory',
  3: 'All Mandatory',
};

const getTodayInTz = (tzCode: string): string => {
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: tzCode,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date());
  } catch {
    return moment().format('YYYY-MM-DD');
  }
};

const isDateExpired = (expirationDate: string, tzCode: string): boolean => {
  const today = getTodayInTz(tzCode);
  return moment(today).isAfter(moment(expirationDate).format('YYYY-MM-DD'));
};

const isDateUpcoming = (effectiveDate: string, tzCode: string): boolean => {
  const today = getTodayInTz(tzCode);
  return moment(today).isBefore(moment(effectiveDate).format('YYYY-MM-DD'));
};

// Returns Sets for O(1) lookups.
// workerItems can be WorkerCertification[], WorkerTraining[], or WorkerCourse[].
const getActiveAndExpiredIds = (
  workerItems: any[],
  tzCode: string,
): { activeIds: Set<string>; expiredIds: Set<string> } => {
  const activeIds = new Set<string>();
  const expiredIds = new Set<string>();

  for (const item of workerItems) {
    const id: string = item?.certification?.id ?? item?.training?.id ?? item?.courseId ?? '';
    if (!id) continue;

    const expirationDate: string | null = item?.expirationDate ?? null;
    const isCourseCompleted: boolean | undefined = item?.isCourseCompleted;

    if (expirationDate && isDateExpired(expirationDate, tzCode)) {
      if (!activeIds.has(id)) expiredIds.add(id);
    } else {
      const isActive = isCourseCompleted ?? true;
      if (isActive) {
        activeIds.add(id);
        expiredIds.delete(id);
      }
    }
  }

  return { activeIds, expiredIds };
};

// Converts learningGroups → same shape as certificationGroups/trainingGroups
// but with a `learnings` array (id = courseId).
const parseLearningGroups = (learningGroups: LearningGroup[]) =>
  learningGroups.map(group => ({
    ...group,
    learnings: group.learningGroupCourses.map(c => ({
      id: c.courseId,
      name: c.courseName,
      alias: c.alias,
    })),
  }));

// Splits groups into current-missing and upcoming-missing.
// itemKey: 'certifications' | 'trainings' | 'learnings'
const splitGroups = (
  groups: any[],
  activeIds: Set<string>,
  exemptedIds: Set<string>,
  itemKey: string,
  tzCode: string,
): { current: any[]; upcoming: any[] } => {
  const current: any[] = [];
  const upcoming: any[] = [];

  for (const group of groups) {
    const { validationType, effectiveDate } = group;
    const isUpcoming = effectiveDate && isDateUpcoming(effectiveDate, tzCode);
    const bucket = isUpcoming ? upcoming : current;
    const vt = +validationType;

    if (vt === 2) {
      // At least one from group must be satisfied
      const hasAny =
        group[itemKey]?.some((item: RequiredItem) => activeIds.has(item.id) || exemptedIds.has(item.id)) ?? false;
      if (!hasAny) bucket.push(group);
    } else if (vt === 3) {
      // All items in group must be satisfied
      const remaining = (group[itemKey] as RequiredItem[])?.filter(
        item => !activeIds.has(item.id) && !exemptedIds.has(item.id),
      );
      if (remaining?.length > 0) bucket.push({ ...group, [itemKey]: remaining });
    }
  }

  // Sort upcoming by effectiveDate ascending
  upcoming.sort((a, b) => +new Date(a.effectiveDate) - +new Date(b.effectiveDate));

  return { current, upcoming };
};

// Flattens groups into MissingItem[].
const flattenGroups = (groups: any[], itemKey: string): MissingItem[] => {
  const result: MissingItem[] = [];
  for (const group of groups) {
    const label = MANDATORY_LABELS[+group.validationType] ?? '';
    for (const item of group[itemKey] ?? []) {
      result.push({
        id: item.id,
        name: item.alias || item.name,
        groupName: group.name,
        mandatoryLabel: label,
      });
    }
  }
  return result;
};

// Wraps upcoming raw groups into UpcomingGroup[] (preserving group-level note fields).
const buildUpcomingGroups = (groups: any[], itemKey: string): UpcomingGroup[] =>
  groups.map(group => ({
    effectiveDate: group.effectiveDate,
    isExistingWorkerForGroup: group.isExistingWorkerForGroup ?? false,
    existingWorkerGracePeriodInDays: group.existingWorkerGracePeriodInDays ?? null,
    newWorkerGracePeriodInDays: group.newWorkerGracePeriodInDays ?? null,
    existingWorkerMandatoryDeadline: group.existingWorkerMandatoryDeadline ?? null,
    newWorkerMandatoryDeadline: group.newWorkerMandatoryDeadline ?? null,
    workerBadgeFirstActivatedAt: group.workerBadgeFirstActivatedAt ?? null,
    items: flattenGroups([group], itemKey),
  }));

// ─── Main Export ───────────────────────────────────────────────────────────────

export const computeRequirements = (response: CompaniesRequirementsResponse): ComputedRequirements => {
  const tzCode = response.timeZone?.code || '';

  const exemptedCerts = new Set<string>(response.allExemptedCertifications ?? []);
  const exemptedTrainings = new Set<string>(response.allExemptedTrainings ?? []);
  const exemptedCourses = new Set<string>(response.allExemptedCourses ?? []);

  const { activeIds: activeCertIds } = getActiveAndExpiredIds(response.workerCertifications ?? [], tzCode);
  const { activeIds: activeTrainingIds } = getActiveAndExpiredIds(response.workerTrainings ?? [], tzCode);
  const { activeIds: activeCourseIds } = getActiveAndExpiredIds(response.workerCourses ?? [], tzCode);

  const parsedLearningGroups = parseLearningGroups(response.learningGroups ?? []);

  const { current: currentCertGroups, upcoming: upcomingCertGroups } = splitGroups(
    response.certificationGroups ?? [],
    activeCertIds,
    exemptedCerts,
    'certifications',
    tzCode,
  );

  const { current: currentTrainingGroups, upcoming: upcomingTrainingGroups } = splitGroups(
    response.trainingGroups ?? [],
    activeTrainingIds,
    exemptedTrainings,
    'trainings',
    tzCode,
  );

  const { current: currentCourseGroups, upcoming: upcomingCourseGroups } = splitGroups(
    parsedLearningGroups,
    activeCourseIds,
    exemptedCourses,
    'learnings',
    tzCode,
  );

  return {
    missingCertifications: flattenGroups(currentCertGroups, 'certifications'),
    missingTrainings: flattenGroups(currentTrainingGroups, 'trainings'),
    missingCourses: flattenGroups(currentCourseGroups, 'learnings'),
    upcomingCertifications: buildUpcomingGroups(upcomingCertGroups, 'certifications'),
    upcomingTrainings: buildUpcomingGroups(upcomingTrainingGroups, 'trainings'),
    upcomingCourses: buildUpcomingGroups(upcomingCourseGroups, 'learnings'),
  };
};
